/**
 * Geolocation helpers for the geo banner + venue tagging features.
 *
 * Design notes:
 * - Permission state is checked via the Permissions API where available
 *   (modern browsers) and falls back gracefully.
 * - "Venue ID" is a coarse lat/lng bucket (3 decimal places ≈ 110m
 *   precision). Good enough to cluster matches happening at the same
 *   gym without needing a real geohash library. Matches anywhere in
 *   the same ~110m square share a venueId, so a tournament running on
 *   multiple courts in one building will cluster correctly.
 * - We deliberately don't reverse-geocode in MVP. Adding that means
 *   another API key + cost; for tournament v1, the banner shows
 *   "matches near you" with no human-readable venue name and that's
 *   fine. A follow-up can add reverse-geocoding when the design
 *   demands it.
 */

import { dlog } from './debug';

const PERMISSION_DENIED = 1;
const POSITION_UNAVAILABLE = 2;
const TIMEOUT = 3;

/**
 * Round to a coarse lat/lng bucket. 2 decimals ≈ 1.1 km at the
 * equator — venue-sized, and large enough to tolerate cross-device
 * GPS variance (desktop wifi-based fixes can be 100s of meters off
 * from phone GPS even at the same physical location).
 *
 * Tradeoff: 1.1 km is wide enough that two unrelated venues within a
 * mile of each other would share a bucket. For tournament use that's
 * acceptable — busy tournament weekends typically only have one
 * event per neighborhood. If false-positive clustering becomes an
 * issue, switch to 3 decimals + adjacent-cell search.
 */
export function computeVenueId(lat, lng) {
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  const r = (n) => Math.round(n * 100) / 100;
  return `${r(lat)},${r(lng)}`;
}

/**
 * Parse a venueId back into lat/lng for distance comparisons.
 */
export function parseVenueId(venueId) {
  if (!venueId || typeof venueId !== 'string') return null;
  const [latStr, lngStr] = venueId.split(',');
  const lat = parseFloat(latStr);
  const lng = parseFloat(lngStr);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return { lat, lng };
}

/**
 * Compute the 3×3 grid of venueIds centered on the given lat/lng.
 * Used by nearby-matches discovery to tolerate cross-device GPS
 * variance landing two devices in adjacent buckets despite being at
 * the same venue. Returns 9 venueId strings (center + 8 neighbors).
 *
 * Bucket size is 2-decimal (≈ 1.1 km at the equator), so the 3×3
 * grid spans ≈ 3.3 km — comfortably wider than any plausible
 * cross-device GPS disagreement.
 */
export function computeVenueIdGrid(lat, lng) {
  if (typeof lat !== 'number' || typeof lng !== 'number') return [];
  if (Number.isNaN(lat) || Number.isNaN(lng)) return [];
  const r = (n) => Math.round(n * 100) / 100;
  const cLat = r(lat);
  const cLng = r(lng);
  const ids = [];
  for (let dLat = -1; dLat <= 1; dLat += 1) {
    for (let dLng = -1; dLng <= 1; dLng += 1) {
      const nLat = r(cLat + dLat * 0.01);
      const nLng = r(cLng + dLng * 0.01);
      ids.push(`${nLat},${nLng}`);
    }
  }
  return ids;
}

/**
 * Query the Permissions API for current geolocation permission.
 * Returns 'granted' | 'prompt' | 'denied' | 'unsupported'.
 * Doesn't itself trigger a permission prompt.
 */
export async function queryGeoPermission() {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return 'unsupported';
  }
  if (!navigator.permissions || !navigator.permissions.query) {
    // Safari historically lacked permissions.query for geolocation. We
    // can't know the state without prompting; treat as 'prompt' so the
    // UI surfaces the opt-in CTA.
    return 'prompt';
  }
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status.state; // 'granted' | 'prompt' | 'denied'
  } catch (err) {
    dlog('geo permission query failed:', err);
    return 'prompt';
  }
}

/**
 * Wrapper around navigator.geolocation.getCurrentPosition that returns
 * a promise resolving to { lat, lng, accuracy } or rejecting with a
 * structured error. Times out after 10s to avoid hanging UI.
 */
export function getCurrentLocation(options = {}) {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject({ code: 'unsupported', message: 'Geolocation not supported' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        let code = 'unknown';
        if (error.code === PERMISSION_DENIED) code = 'denied';
        else if (error.code === POSITION_UNAVAILABLE) code = 'unavailable';
        else if (error.code === TIMEOUT) code = 'timeout';
        reject({ code, message: error.message });
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 5 * 60 * 1000, // 5 min cache acceptable
        ...options,
      }
    );
  });
}

/**
 * Haversine distance in meters between two lat/lng points. Used when
 * we want a "nearby" filter finer than the venueId bucket (e.g., to
 * decide which other venueIds are close enough to include).
 */
export function distanceMeters(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000; // Earth radius in meters
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}
